import React, { useState, useEffect, useMemo } from 'react';
import { collection, getDocs, query, where, orderBy, Timestamp } from 'firebase/firestore';
import { db } from '../../../../config/firebase';
import { useAuth } from '../../../../contexts/AuthContext';
import ExportDropdown from './ExportDropdown';

/**
 * Attendance Report Component
 *
 * Displays broad view of past appointment statuses
 * Shows client appointments with date, location, and status information
 *
 * Features:
 * - Date range picker
 * - Client filter dropdown (All clients)
 * - Location filter dropdown (All locations)
 * - Status filter dropdown (All statuses)
 * - Summary row showing totals
 * - Table with Client, Date of service, Location, Status columns
 * - Status displayed with color coding
 * - Pagination controls
 * - Export dropdown (CSV/Excel)
 * - "Learn more" link
 */

interface AttendanceData {
  clientName: string;
  clientId: string;
  dateOfService: string;
  location: string;
  status: string;
}

interface AttendanceReportProps {
  onBack?: () => void;
}

function formatDateForInput(date: Date): string {
  return date.toISOString().split('T')[0]; // YYYY-MM-DD for date input
}

function formatDateDisplay(date: Date): string {
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  const y = date.getFullYear();
  return `${m}/${d}/${y}`;
}

function mapStatus(raw: string): string {
  switch ((raw || '').toLowerCase()) {
    case 'completed': return 'Show';
    case 'no-show':
    case 'noshow': return 'No Show';
    case 'cancelled':
    case 'canceled': return 'Cancelled';
    case 'scheduled':
    case 'confirmed': return 'Scheduled';
    default: return raw || 'Unknown';
  }
}

function mapLocation(modality: string): string {
  switch ((modality || '').toLowerCase()) {
    case 'video':
    case 'telehealth': return 'Video Office';
    default: return 'In-Person Office';
  }
}

const AttendanceReport: React.FC<AttendanceReportProps> = ({ onBack }) => {
  const { user } = useAuth();

  const defaultEnd = new Date();
  const defaultStart = new Date();
  defaultStart.setDate(defaultStart.getDate() - 30);

  const [startDate, setStartDate] = useState(formatDateForInput(defaultStart));
  const [endDate, setEndDate] = useState(formatDateForInput(defaultEnd));
  const [clientFilter, setClientFilter] = useState('all');
  const [locationFilter, setLocationFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [data, setData] = useState<AttendanceData[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user?.uid) return;

    const fetchAppointments = async () => {
      setLoading(true);
      try {
        const start = new Date(startDate + 'T00:00:00');
        const end = new Date(endDate + 'T23:59:59');

        const appointmentsRef = collection(db, 'appointments');
        const q = query(
          appointmentsRef,
          where('providerId', '==', user.uid),
          where('appointmentDate', '>=', Timestamp.fromDate(start)),
          where('appointmentDate', '<=', Timestamp.fromDate(end)),
          orderBy('appointmentDate', 'desc')
        );

        const snapshot = await getDocs(q);
        const results: AttendanceData[] = [];

        snapshot.forEach((docSnap) => {
          const d = docSnap.data();
          let apptDate: Date | null = null;

          if (d.appointmentDate?.toDate) {
            apptDate = d.appointmentDate.toDate();
          } else if (d.appointmentDate) {
            apptDate = new Date(d.appointmentDate);
          }

          const clientName = d.patientName || d.clientName || 'Unknown Client';
          const clientId = d.patientId || d.clientId || docSnap.id;

          results.push({
            clientName,
            clientId,
            dateOfService: apptDate ? formatDateDisplay(apptDate) : 'N/A',
            location: mapLocation(d.modality || d.location || ''),
            status: mapStatus(d.status || ''),
          });
        });

        setData(results);
      } catch (err) {
        console.error('Error fetching attendance data:', err);
        setData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchAppointments();
  }, [user?.uid, startDate, endDate]);

  // Apply filters
  const filteredData = useMemo(() => {
    return data.filter((item) => {
      if (clientFilter !== 'all' && item.clientId !== clientFilter) return false;
      if (locationFilter !== 'all') {
        if (locationFilter === 'video' && item.location !== 'Video Office') return false;
        if (locationFilter === 'office' && item.location !== 'In-Person Office') return false;
      }
      if (statusFilter !== 'all') {
        if (statusFilter === 'show' && item.status !== 'Show') return false;
        if (statusFilter === 'no-show' && item.status !== 'No Show') return false;
        if (statusFilter === 'cancelled' && item.status !== 'Cancelled') return false;
        if (statusFilter === 'scheduled' && item.status !== 'Scheduled') return false;
      }
      return true;
    });
  }, [data, clientFilter, locationFilter, statusFilter]);

  // Unique clients for dropdown
  const uniqueClientList = useMemo(() => {
    const map = new Map<string, string>();
    data.forEach((d) => map.set(d.clientId, d.clientName));
    return Array.from(map.entries()); // [clientId, clientName][]
  }, [data]);

  // Pagination
  const totalPages = Math.max(1, Math.ceil(filteredData.length / rowsPerPage));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const paginatedData = filteredData.slice(
    (safeCurrentPage - 1) * rowsPerPage,
    safeCurrentPage * rowsPerPage
  );
  const rangeStart = filteredData.length === 0 ? 0 : (safeCurrentPage - 1) * rowsPerPage + 1;
  const rangeEnd = Math.min(safeCurrentPage * rowsPerPage, filteredData.length);

  // Summary counts from filtered data
  const uniqueClients = new Set(filteredData.map(d => d.clientId)).size;
  const totalAppointments = filteredData.length;
  const uniqueLocations = new Set(filteredData.map(d => d.location)).size;
  const uniqueStatuses = new Set(filteredData.map(d => d.status)).size;

  return (
    <div style={{
      backgroundColor: '#ffffff',
      minHeight: '100vh',
      padding: '1rem'
    }}>
      {/* Breadcrumb */}
      <div style={{
        fontSize: '0.875rem',
        color: '#d1d5db',
        marginBottom: '1rem'
      }}>
        <button
          onClick={onBack}
          style={{
            background: 'none',
            border: 'none',
            color: '#d1d5db',
            textDecoration: 'none',
            cursor: 'pointer',
            padding: 0,
            font: 'inherit'
          }}
        >
          Analytics
        </button>
        {' / '}
        <span style={{ color: '#111827' }}>Attendance</span>
      </div>

      {/* Page Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: '1.5rem'
      }}>
        <div>
          <h1 style={{
            fontSize: '1.875rem',
            fontWeight: '700',
            color: '#111827',
            margin: 0,
            marginBottom: '0.5rem'
          }}>
            Attendance
          </h1>
          <p style={{
            fontSize: '0.875rem',
            color: '#d1d5db',
            margin: 0
          }}>
            Broad view of past appointment statuses.{' '}
            <span style={{ color: '#4338ca', cursor: 'default' }}>
              Learn more
            </span>
          </p>
        </div>
        <ExportDropdown />
      </div>

      {/* Filters */}
      <div style={{
        display: 'flex',
        gap: '1rem',
        marginBottom: '1.5rem',
        alignItems: 'center',
        flexWrap: 'wrap'
      }}>
        {/* Date Range Picker */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          border: '1px solid #d1d5db',
          borderRadius: '4px',
          padding: '0.5rem 0.75rem',
          backgroundColor: '#ffffff'
        }}>
          <span style={{ fontSize: '1rem', color: '#2563eb' }}>📅</span>
          <input
            type="date"
            value={startDate}
            onChange={(e) => { setStartDate(e.target.value); setCurrentPage(1); }}
            style={{
              border: 'none',
              outline: 'none',
              fontSize: '0.875rem',
              color: '#111827',
              cursor: 'pointer'
            }}
          />
          <span style={{ color: '#6b7280' }}>–</span>
          <input
            type="date"
            value={endDate}
            onChange={(e) => { setEndDate(e.target.value); setCurrentPage(1); }}
            style={{
              border: 'none',
              outline: 'none',
              fontSize: '0.875rem',
              color: '#111827',
              cursor: 'pointer'
            }}
          />
        </div>

        {/* Client Filter */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem'
        }}>
          <span style={{ fontSize: '1rem', color: '#d1d5db' }}>👤</span>
          <select
            value={clientFilter}
            onChange={(e) => { setClientFilter(e.target.value); setCurrentPage(1); }}
            style={{
              border: '1px solid #d1d5db',
              borderRadius: '4px',
              padding: '0.5rem 0.75rem',
              fontSize: '0.875rem',
              color: '#111827',
              backgroundColor: '#ffffff',
              cursor: 'pointer'
            }}
          >
            <option value="all">All clients</option>
            {uniqueClientList.map(([id, name]) => (
              <option key={id} value={id}>{name}</option>
            ))}
          </select>
        </div>

        {/* Location Filter */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem'
        }}>
          <span style={{ fontSize: '1rem', color: '#d1d5db' }}>📍</span>
          <select
            value={locationFilter}
            onChange={(e) => { setLocationFilter(e.target.value); setCurrentPage(1); }}
            style={{
              border: '1px solid #d1d5db',
              borderRadius: '4px',
              padding: '0.5rem 0.75rem',
              fontSize: '0.875rem',
              color: '#111827',
              backgroundColor: '#ffffff',
              cursor: 'pointer'
            }}
          >
            <option value="all">All locations</option>
            <option value="video">Video Office</option>
            <option value="office">In-Person Office</option>
          </select>
        </div>

        {/* Status Filter */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem'
        }}>
          <span style={{ fontSize: '1rem', color: '#d1d5db' }}>☰</span>
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
            style={{
              border: '1px solid #d1d5db',
              borderRadius: '4px',
              padding: '0.5rem 0.75rem',
              fontSize: '0.875rem',
              color: '#111827',
              backgroundColor: '#ffffff',
              cursor: 'pointer'
            }}
          >
            <option value="all">All statuses</option>
            <option value="show">Show</option>
            <option value="no-show">No Show</option>
            <option value="cancelled">Cancelled</option>
            <option value="scheduled">Scheduled</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div style={{
        border: '1px solid #e5e7eb',
        borderRadius: '6px',
        overflow: 'hidden',
        marginBottom: '1rem'
      }}>
        <table style={{
          width: '100%',
          borderCollapse: 'collapse'
        }}>
          <thead>
            <tr style={{
              backgroundColor: '#f9fafb',
              borderBottom: '1px solid #e5e7eb'
            }}>
              <th style={{
                padding: '0.75rem 1rem',
                textAlign: 'left',
                fontSize: '0.75rem',
                fontWeight: '600',
                color: '#374151',
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}>
                Client
              </th>
              <th style={{
                padding: '0.75rem 1rem',
                textAlign: 'left',
                fontSize: '0.75rem',
                fontWeight: '600',
                color: '#374151',
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}>
                Date of service
              </th>
              <th style={{
                padding: '0.75rem 1rem',
                textAlign: 'left',
                fontSize: '0.75rem',
                fontWeight: '600',
                color: '#374151',
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}>
                Location
              </th>
              <th style={{
                padding: '0.75rem 1rem',
                textAlign: 'left',
                fontSize: '0.75rem',
                fontWeight: '600',
                color: '#374151',
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}>
                Status
              </th>
            </tr>
          </thead>
          <tbody>
            {/* Summary Row */}
            <tr style={{
              backgroundColor: '#f3f4f6',
              borderBottom: '1px solid #e5e7eb'
            }}>
              <td style={{
                padding: '0.75rem 1rem',
                fontSize: '0.875rem',
                fontWeight: '500',
                color: '#111827'
              }}>
                {uniqueClients} client
              </td>
              <td style={{
                padding: '0.75rem 1rem',
                fontSize: '0.875rem',
                fontWeight: '500',
                color: '#111827'
              }}>
                {totalAppointments} appointment
              </td>
              <td style={{
                padding: '0.75rem 1rem',
                fontSize: '0.875rem',
                fontWeight: '500',
                color: '#111827'
              }}>
                {uniqueLocations} office
              </td>
              <td style={{
                padding: '0.75rem 1rem',
                fontSize: '0.875rem',
                fontWeight: '500',
                color: '#111827'
              }}>
                {uniqueStatuses} status
              </td>
            </tr>

            {/* Loading State */}
            {loading && (
              <tr>
                <td colSpan={4} style={{
                  padding: '2rem',
                  textAlign: 'center',
                  fontSize: '0.875rem',
                  color: '#6b7280'
                }}>
                  Loading attendance data...
                </td>
              </tr>
            )}

            {/* Data Rows */}
            {!loading && paginatedData.map((item, index) => (
              <tr key={index} style={{
                borderBottom: index < paginatedData.length - 1 ? '1px solid #e5e7eb' : 'none',
                backgroundColor: '#ffffff'
              }}>
                <td style={{
                  padding: '0.75rem 1rem',
                  fontSize: '0.875rem'
                }}>
                  <span style={{
                    color: '#1a56db',
                    fontWeight: 500
                  }}>
                    {item.clientName}
                  </span>
                </td>
                <td style={{
                  padding: '0.75rem 1rem',
                  fontSize: '0.875rem',
                  color: '#111827'
                }}>
                  {item.dateOfService}
                </td>
                <td style={{
                  padding: '0.75rem 1rem',
                  fontSize: '0.875rem',
                  color: '#111827',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <span style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    backgroundColor: item.location === 'Video Office' ? '#8b5cf6' : '#f59e0b',
                    display: 'inline-block'
                  }}></span>
                  {item.location}
                </td>
                <td style={{
                  padding: '0.75rem 1rem',
                  fontSize: '0.875rem'
                }}>
                  <span style={{
                    color: item.status === 'Show' ? '#059669'
                      : item.status === 'No Show' ? '#dc2626'
                      : item.status === 'Cancelled' ? '#d97706'
                      : '#2563eb',
                    fontWeight: '500'
                  }}>
                    {item.status}
                  </span>
                </td>
              </tr>
            ))}

            {/* Empty State */}
            {!loading && filteredData.length === 0 && (
              <tr>
                <td colSpan={4} style={{
                  padding: '2rem',
                  textAlign: 'center',
                  fontSize: '0.875rem',
                  color: '#d1d5db'
                }}>
                  No attendance data available for the selected filters
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        fontSize: '0.875rem',
        color: '#d1d5db'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span>Rows per page</span>
          <select
            value={rowsPerPage}
            onChange={(e) => { setRowsPerPage(Number(e.target.value)); setCurrentPage(1); }}
            style={{
              border: '1px solid #d1d5db',
              borderRadius: '4px',
              padding: '0.25rem 0.5rem',
              fontSize: '0.875rem',
              backgroundColor: '#ffffff'
            }}
          >
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <span>{rangeStart}-{rangeEnd} of {filteredData.length}</span>
          <div style={{ display: 'flex', gap: '0.25rem' }}>
            <button
              disabled={safeCurrentPage <= 1}
              onClick={() => setCurrentPage(1)}
              style={{
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                padding: '0.25rem 0.5rem',
                backgroundColor: '#ffffff',
                cursor: safeCurrentPage <= 1 ? 'not-allowed' : 'pointer',
                color: safeCurrentPage <= 1 ? '#d1d5db' : '#374151'
              }}
            >
              ⏮
            </button>
            <button
              disabled={safeCurrentPage <= 1}
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              style={{
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                padding: '0.25rem 0.5rem',
                backgroundColor: '#ffffff',
                cursor: safeCurrentPage <= 1 ? 'not-allowed' : 'pointer',
                color: safeCurrentPage <= 1 ? '#d1d5db' : '#374151'
              }}
            >
              ◀
            </button>
            <button
              disabled={safeCurrentPage >= totalPages}
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              style={{
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                padding: '0.25rem 0.5rem',
                backgroundColor: '#ffffff',
                cursor: safeCurrentPage >= totalPages ? 'not-allowed' : 'pointer',
                color: safeCurrentPage >= totalPages ? '#d1d5db' : '#374151'
              }}
            >
              ▶
            </button>
            <button
              disabled={safeCurrentPage >= totalPages}
              onClick={() => setCurrentPage(totalPages)}
              style={{
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                padding: '0.25rem 0.5rem',
                backgroundColor: '#ffffff',
                cursor: safeCurrentPage >= totalPages ? 'not-allowed' : 'pointer',
                color: safeCurrentPage >= totalPages ? '#d1d5db' : '#374151'
              }}
            >
              ⏭
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AttendanceReport;
